import type { ArtifactManifest, DrawingDocument } from '../contract/types.js';
import { sha256Hex } from '../artefacts/hash.js';
import type { ChangeClass, DiffResult, PageAlignmentEntry } from './diff.js';
import { diffEdges, type DiffEdge } from './edges.js';
import { renderDiffOverlay } from './overlay.js';
import type { DiffIdentity } from './pair.js';

/**
 * The comparison's artefacts.
 *
 * Three of them, mirroring the four a single conversion produces: `diff.json`
 * carries the comparison, one `diff-overlay` SVG carries each aligned sheet, and
 * `diff-metrics.json` carries the numbers a reviewer or a gate reads without
 * parsing the entries.
 *
 * **`diff.json` is a locator, not a copy.** Each side of a change names the
 * version, the sheet, the entity identifier and the source bounding box — and
 * stops there. It does not carry the entity's properties or provenance, because
 * those already exist, in that version's own `drawing.json`, and duplicating
 * them would create a second copy free to disagree with the first. So reading a
 * comparison means holding all three files. That is stated here, and in the
 * artefact itself, rather than left for someone to discover when the copy has
 * drifted.
 */

export interface DiffMetrics {
  identity: DiffIdentity;
  comparisonSpace: DiffResult['comparisonSpace'];
  partial: boolean;
  tolerance: number;
  displacementUnit: DiffResult['displacementUnit'];
  basePageCount: number;
  comparePageCount: number;
  baseEntityCount: number;
  compareEntityCount: number;
  byClass: Record<ChangeClass, number>;
  byBasePage: Record<number, Partial<Record<ChangeClass, number>>>;
  byComparePage: Record<number, Partial<Record<ChangeClass, number>>>;
  ambiguous: number;
  coverage: number;
  pageAlignment: PageAlignmentEntry[];
  /** States a summary alone would misrepresent. See `diff/edges.ts`. */
  edges: DiffEdge[];
}

export interface DiffArtefacts {
  diffJson: string;
  /** One per aligned sheet, in alignment order. */
  overlays: string[];
  metrics: string;
  manifests: ArtifactManifest[];
}

/**
 * Sheet artefacts are keyed `s1`, `s2` and not `p1`, `p2`.
 *
 * A comparison sheet is a *pair* of sheets, and after an insertion the two
 * versions number the same sheet differently — so a key that looked like a page
 * number would be a page number in neither document.
 */
export function overlayKey(sheetIndex: number, sheetCount: number): string {
  return sheetCount <= 1 ? 'diff-overlay.svg' : `diff-overlay.s${sheetIndex + 1}.svg`;
}

export async function buildDiffArtefacts(
  base: DrawingDocument,
  compare: DrawingDocument,
  diff: DiffResult,
  identity: DiffIdentity,
  commit?: string,
): Promise<DiffArtefacts> {
  // The identity says which matcher produced this comparison, and the artefacts
  // are stored under a key derived from it. Nothing else checks that the two
  // agree, so a caller that validated a pair under one profile and compared
  // under another would publish artefacts labelled with a matcher that did not
  // produce them — and the label is the only thing a reader has.
  if (identity.matcherProfileVersion !== diff.matcherProfileVersion) {
    throw new Error(
      `the comparison was produced by matcher profile ${diff.matcherProfileVersion} but the ` +
        `identity names ${identity.matcherProfileVersion}; the artefacts would be labelled with a ` +
        'matcher that did not produce them',
    );
  }
  if (identity.schemaVersion !== diff.schemaVersion) {
    throw new Error(
      `the comparison names contract ${diff.schemaVersion} but the identity names ` +
        `${identity.schemaVersion}`,
    );
  }

  const sheets = diff.pageAlignment.length;
  const overlays = diff.pageAlignment.map((_, index) =>
    renderDiffOverlay(base, compare, diff, index, {
      baseArtefactSha256: identity.base.artefactSha256,
      compareArtefactSha256: identity.compare.artefactSha256,
      matcherProfileVersion: identity.matcherProfileVersion,
      ...(commit === undefined ? {} : { commit }),
    }),
  );

  const metrics: DiffMetrics = {
    identity,
    comparisonSpace: diff.comparisonSpace,
    partial: diff.partial,
    tolerance: diff.tolerance,
    displacementUnit: diff.displacementUnit,
    basePageCount: diff.basePageCount,
    comparePageCount: diff.comparePageCount,
    baseEntityCount: diff.baseEntityCount,
    compareEntityCount: diff.compareEntityCount,
    byClass: diff.summary.byClass,
    byBasePage: diff.summary.byBasePage,
    byComparePage: diff.summary.byComparePage,
    ambiguous: diff.summary.ambiguous,
    coverage: diff.summary.coverage,
    pageAlignment: diff.pageAlignment,
    edges: diffEdges(diff),
  };

  const diffJson = JSON.stringify(
    {
      note:
        'Each side of a change is a locator into that version. The entity properties and ' +
        "provenance live in that version's own drawing.json and are deliberately not copied here.",
      identity,
      diff,
    },
    null,
    2,
  );
  const metricsJson = JSON.stringify(metrics, null, 2);

  const parts = [
    { key: 'diff.json', type: 'application/json', body: diffJson },
    ...overlays.map((svg, index) => ({
      key: overlayKey(index, sheets),
      type: 'image/svg+xml',
      body: svg,
    })),
    { key: 'diff-metrics.json', type: 'application/json', body: metricsJson },
  ];

  const manifests: ArtifactManifest[] = [];
  for (const part of parts) {
    manifests.push({
      contentType: part.type,
      sha256: await sha256Hex(part.body),
      bytes: new TextEncoder().encode(part.body).byteLength,
      storageKey: `${identity.storageKey}/${part.key}`,
      schemaVersion: identity.schemaVersion,
      // A comparison's tuning is the matcher's, not the extractor's.
      profileVersion: identity.matcherProfileVersion,
      // Two source documents, one field. A difference is carried into the
      // manifest rather than resolved out of sight, because it is the reason a
      // comparison can show changes nobody made.
      extractorBundleVersion: identity.extractorBundleMismatch
        ? `${identity.base.extractorBundleVersion}+${identity.compare.extractorBundleVersion}`
        : identity.base.extractorBundleVersion,
    });
  }

  return { diffJson, overlays, metrics: metricsJson, manifests };
}
